// LivePreview — tiny LiveKit subscriber that grabs ONE camera frame from a live stream
// and renders it as a muted, autoplay video tile. Designed for thumbnail-sized
// previews on Explore / Spotlight / Profile cards.
//
// Connections are lightweight (subscribe-only, video-only, lowest-quality)
// but we cap concurrent previews on a single page via a tiny browser-global queue
// to avoid hammering LiveKit when 30+ cards render.
import { useEffect, useState } from "react";
import { LiveKitRoom, useTracks, VideoTrack } from "@livekit/components-react";
import { Track, ConnectionQuality } from "livekit-client";
import api from "../lib/api";

const MAX_CONCURRENT = 6;

if (typeof window !== "undefined" && !window.__nexusLivePreviewSlots) {
  window.__nexusLivePreviewSlots = { active: 0 };
}

function PreviewVideo() {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }]);
  const first = tracks[0];
  if (!first) return null;
  return <VideoTrack trackRef={first} muted playsInline className="absolute inset-0 w-full h-full object-cover" />;
}

export default function LivePreview({ streamId, fallback, className = "" }) {
  const [tok, setTok] = useState(null);
  const [url, setUrl] = useState(null);
  const [slot, setSlot] = useState(false);

  useEffect(() => {
    if (!streamId) return;
    let cancel = false;
    // Attempt to claim a slot
    const tryClaim = () => {
      const s = window.__nexusLivePreviewSlots;
      if (s.active < MAX_CONCURRENT) {
        s.active += 1;
        setSlot(true);
        return true;
      }
      return false;
    };
    if (!tryClaim()) {
      // Retry every 1500ms until a slot opens or the component unmounts
      const t = setInterval(() => { if (tryClaim()) clearInterval(t); }, 1500);
      return () => { clearInterval(t); if (slot) window.__nexusLivePreviewSlots.active -= 1; };
    }

    api.post("/livekit/token", { stream_id: streamId, role: "viewer", preview: true })
      .then(({ data }) => { if (!cancel) { setTok(data.token); setUrl(data.url); } })
      .catch(() => {});

    return () => {
      cancel = true;
      if (slot) window.__nexusLivePreviewSlots.active -= 1;
    };
  }, [streamId]);

  if (!tok || !url) return fallback || null;

  return (
    <div className={`absolute inset-0 ${className}`} data-testid={`live-preview-${streamId}`}>
      <LiveKitRoom
        token={tok}
        serverUrl={url}
        connect
        audio={false}
        video={false}
        options={{ adaptiveStream: true, dynacast: true }}
        style={{ height: "100%", background: "transparent" }}
      >
        <PreviewVideo />
      </LiveKitRoom>
    </div>
  );
}
